import axios from 'axios';
import getConfig from './getConfig';

const EXPIRY_BUFFER_MS = 30_000;
const KEYCLOAK_TOKEN_PATH = '/realms/validsign/protocol/openid-connect/token';

interface TokenCache {
    accessToken: string;
    expiresAt: number;
}

interface OAuthTokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
}

/**
 * Resolves the full token URL from an IDP base URL or legacy full URL.
 * If the URL already contains a path (legacy format from local* configs),
 * it is used as-is. Otherwise, appends the standard Keycloak token path.
 */
export function getTokenUrl(idpUrl: string): string {
    const urlObj = new URL(idpUrl);
    const hasPath = urlObj.pathname !== '/' && urlObj.pathname !== '';

    if (hasPath) {
        return idpUrl;
    }

    const normalizedUrl = idpUrl.replace(/\/+$/, '');
    return `${normalizedUrl}${KEYCLOAK_TOKEN_PATH}`;
}

/**
 * Extracts the base URL (protocol + host) from an IDP URL.
 * Strips any path, returning only the origin portion.
 */
export function getIdpBaseUrl(idpUrl: string): string {
    const urlObj = new URL(idpUrl);
    return `${urlObj.protocol}//${urlObj.host}`;
}

/**
 * Manages server-to-server OAuth2 tokens using client_credentials grant.
 * Handles token caching with automatic refresh before expiry.
 */
class S2STokenManager {
    private cache: TokenCache | null = null;

    private refreshPromise: Promise<string> | null = null;

    async getAccessToken(): Promise<string> {
        if (this.getIsTokenValid() && this.cache) {
            return this.cache.accessToken;
        }
        return this.refreshToken();
    }

    private getIsTokenValid(): boolean {
        if (!this.cache) {
            return false;
        }
        const bufferExpiryMs = this.cache.expiresAt - EXPIRY_BUFFER_MS;
        return Date.now() < bufferExpiryMs;
    }

    private async refreshToken(): Promise<string> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.fetchNewToken();
        try {
            return await this.refreshPromise;
        } finally {
            this.refreshPromise = null;
        }
    }

    private async fetchNewToken(): Promise<string> {
        const {default: config} = await getConfig();
        const idpUrl = config.MICROSERVICES_URLS?.IDP;

        if (!idpUrl) {
            throw new Error('MICROSERVICES_URLS.IDP is not configured');
        }

        const clientId = process.env.S2S_CLIENT_ID;
        const clientSecret = process.env.S2S_CLIENT_SECRET;

        if (!clientId || !clientSecret) {
            throw new Error('S2S_CLIENT_ID and S2S_CLIENT_SECRET environment variables are required');
        }

        const tokenUrl = getTokenUrl(idpUrl);

        const params = new URLSearchParams();
        params.append('grant_type', 'client_credentials');
        params.append('client_id', clientId);
        params.append('client_secret', clientSecret);

        const response = await axios.post<OAuthTokenResponse>(tokenUrl, params.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        const {access_token: accessToken, expires_in: expiresInSec} = response.data;
        const expiresAt = Date.now() + expiresInSec * 1000;

        this.cache = {accessToken, expiresAt};

        return accessToken;
    }
}

export const s2sTokenManager = new S2STokenManager();
